// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { parseBoolean } from "@turnerBlueprint/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface Props {
    variantSelection: SafeDictionary<string>;
    variantTrait: VariantTraitModel;
    updateVariantSelection: (traitValueId: string, variantTraitId: string) => void;
}

// XNG-Change TSCUX-167
export const mapStateToProps = (state: ApplicationState) => ({
    showVariantAttributesWithSingleValue: parseBoolean(
        (getSettingsCollection(state).customSettings as any)?.showVariantAttributesWithSingleValue,
    ),
});

export interface ProductDetailsVariantDropdownStyles {
    select?: SelectPresentationProps;
}

export const productDetailsVariantDropdownStyles: ProductDetailsVariantDropdownStyles = {
    select: {
        cssOverrides: {
            formField: css`
                margin-top: 10px;
            `,
        },
    },
};

const styles = productDetailsVariantDropdownStyles;

const ProductDetailsVariantDropdown = ({
    variantSelection,
    updateVariantSelection,
    variantTrait,
    showVariantAttributesWithSingleValue,
}: Props & ReturnType<typeof mapStateToProps>) => {
    // XNG-Change: TSCUX-167
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (variantTrait.traitValues && variantTrait.traitValues.length === 1) {
                updateVariantSelection(variantTrait.traitValues[0].id, variantTrait.id);
            }
        }, 150);

        return () => clearTimeout(timeoutId);
    }, []);

    // XNG-Change
    if (!showVariantAttributesWithSingleValue && variantTrait.traitValues && variantTrait.traitValues.length === 1) {
        return null;
    }

    return (
        <Select
            {...styles.select}
            key={variantTrait.id}
            label={variantTrait.nameDisplay}
            value={variantSelection[variantTrait.id]}
            onChange={event => {
                updateVariantSelection(event.currentTarget.value, variantTrait.id);
            }}
            data-test-selector={`styleSelect_${variantTrait.name}`}
        >
            <option value="" key={variantTrait.id}>
                {variantTrait.unselectedValue
                    ? variantTrait.unselectedValue
                    : `${translate("Select")} ${variantTrait.nameDisplay}`}
            </option>
            {variantTrait.traitValues?.map(traitValue => (
                <option value={`${traitValue.id}`} key={`${traitValue.id}`} disabled={traitValue.isDisabled}>
                    {traitValue.valueDisplay}
                </option>
            ))}
        </Select>
    );
};

export default connect(mapStateToProps)(ProductDetailsVariantDropdown);
